
import liff from '@line/liff';
import { defineComponent, onBeforeMount, reactive, provide, computed, ComputedRef, ref } from 'vue';
import { useRoute } from 'vue-router/composables';
import TheTabBar from '@/components/molecules/TheTabBar.vue';
import ErrorBoundary from '@/components/molecules/ErrorBoundary.vue';
import RequireRegistrationTemplate from '@/components/templates/RequireRegistrationTemplate.vue';
import { useLiff } from '@/compositions/useLiff';
import * as Sentry from '@sentry/vue';
import { customer as customerMock } from '@/../mocks/liff/login';
import { useDevelopment } from '@/compositions/useDevelopment';
import { MultipleCouponKey, useMultipleCoupon } from '@/compositions/useMultipleCoupon';
import { usePoint } from './compositions/usePoint';
import { useData } from '@/compositions/useData';
import TroubleshootingModal from '@/components/molecules/TroubleshootingModal.vue';

// NOTE: local時はdebug用に.env.localから値注入
const LIFF_ID = process.env.NODE_ENV === 'local' ? window.LIFF_ID || process.env.VUE_APP_LIFF_ID : window.LIFF_ID;
const ORG_ID = process.env.NODE_ENV === 'local' ? window.ORG_ID || process.env.VUE_APP_ORG_ID : window.ORG_ID;
const CHANNEL_ID =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;
const footerContentSettingsJson: string =
  process.env.NODE_ENV === 'local'
    ? window.FOOTER_CONTENT_SETTINGS || process.env.VUE_APP_FOOTER_CONTENT_SETTINGS
    : window.FOOTER_CONTENT_SETTINGS;

const footerContentSettings: {
  customerCardEnabled: boolean;
  couponEnabled: boolean;
  exchangeCouponEnabled: boolean;
  pointHistoryEnabled: boolean;
} = JSON.parse(footerContentSettingsJson);

type State = {
  isLoggedIn: boolean;
  requireRegistration: boolean;
  error?: Error;
};

export default defineComponent({
  name: 'App',

  components: {
    TheTabBar,
    ErrorBoundary,
    RequireRegistrationTemplate,
    TroubleshootingModal,
  },

  setup(_) {
    provide(MultipleCouponKey, useMultipleCoupon());

    const { login } = useLiff();
    const { enabled: pointEnabled } = usePoint();
    const route = useRoute();
    const data = useData();

    const state = reactive<State>({
      isLoggedIn: false,
      requireRegistration: false,
      error: undefined,
    });

    // フッター設定 ON/OFF
    const _FOOTER_DISABLED: boolean =
      `${
        process.env.NODE_ENV === 'local'
          ? window.FOOTER_DISABLED || process.env.VUE_APP_FOOTER_DISABLED
          : window.FOOTER_DISABLED
      }` === 'true';

    // フッターコンテンツ設定 全てOFFの時、フッター非表示
    const _isFooter: ComputedRef<boolean> = computed(() =>
      [
        footerContentSettings.customerCardEnabled,
        footerContentSettings.couponEnabled,
        footerContentSettings.exchangeCouponEnabled,
        footerContentSettings.pointHistoryEnabled && pointEnabled.value,
      ].some(v => v)
    );

    const isFooter: boolean = !_FOOTER_DISABLED && _isFooter.value;

    // 会員情報設定が必要な場合は、訴求画面に遷移
    const encourageRegistrationIfNeeded = (customer: {
      registered: boolean;
      isRegistrationRequired: boolean;
      surveyUrl: string;
    }) => {
      data.set('surveyUrl', customer.surveyUrl);
      if (customer.isRegistrationRequired && !customer.registered) {
        state.requireRegistration = true;
      }
    };

    /**
     * CS-6617 IDToken有効期限問題 トークン系キャッシュの削除
     */
    const _clearLiffStore = () => {
      const storage = liff.isInClient() ? sessionStorage : localStorage;

      // check expired
      const decodedIDTokenString = storage.getItem(`LIFF_STORE:${LIFF_ID}:decodedIDToken`);
      if (!decodedIDTokenString) {
        return;
      }
      const decodedIDToken = JSON.parse(decodedIDTokenString);
      if (new Date().getTime() <= decodedIDToken.exp * 1000) {
        // 有効期限内
        return;
      }

      // clear
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++)
        if (storage.key(i)?.indexOf(`LIFF_STORE:${LIFF_ID}`) === 0) keys.push(storage.key(i)!);
      for (const key of keys) storage.removeItem(key);
    };

    const showHelpModal = ref(false);
    const toggleHelpModal = () => {
      showHelpModal.value = !showHelpModal.value;
    };

    onBeforeMount(async () => {
      try {
        if (LIFF_ID) {
          // LIFFが存在する => LIFFモード
          console.info('LIFF mode');

          // CS-6617 liff.initの前にトークンキャッシュをクリア。LIFFであれば挙動に変化なし。webは毎回LINEログインへ遷移する
          _clearLiffStore(); // 202230703 クーポン取得エラー報告によりコメントアウト
          await liff.init({ liffId: LIFF_ID });

          if (liff.isLoggedIn()) {
            // ログイン済み

            // idTokenをPOSTしてバックエンド側でLINEユーザー特定 & start session
            const idToken = liff.getIDToken();
            if (!idToken) throw new Error('Failed to get LIFF ID token');
            const gwKey = route.query.gw_key as string;
            const { customer } = await login(idToken, gwKey);
            if (!customer) throw new Error('LIFF Login failed. customer not found.');
            const redirectPath = route.fullPath;

            // ポリシー同意が必要な場合はリダイレクト
            if (customer.isPolicyConsentRequired) {
              location.replace(
                `/policy/${ORG_ID}/consent?redirect_path=${encodeURIComponent(`/${CHANNEL_ID}/retail${redirectPath}`)}`
              );
              return;
            }

            // 顧客ログイン完了 - LIFF経由
            state.isLoggedIn = true;

            // Sentryにユーザ情報をセットする
            Sentry.setUser({ id: customer.id /* email, username */ });

            // 会員情報設定が必要な場合は、訴求画面に遷移
            encourageRegistrationIfNeeded(customer);
          } else {
            // 未ログイン
            liff.login({ redirectUri: location.href }); // ログイン後に現在のページに戻る
          }
        } else {
          // LIFFが存在しない => WebAppモード
          console.info('WebApp mode');

          // バックエンド側からCustomer情報が注入される
          let customer: { id: string; registered: boolean; isRegistrationRequired: boolean; surveyUrl: string };
          if (process.env.NODE_ENV !== 'local') {
            customer = JSON.parse(decodeURIComponent(escape(atob(window.customer))));
          } else {
            // NOTE: local時はdebug用にCustomer偽装
            // mock利用 or local起動app接続
            if (process.env.VUE_APP_LOCAL_LINE_USER_ID) {
              customer = await useDevelopment().login();
            } else {
              customer = customerMock;
            }
          }

          // 顧客ログイン完了 - LINEログイン経由
          state.isLoggedIn = true;

          // Sentryにユーザ情報をセットする
          Sentry.setUser({ id: customer.id /* email, username */ });

          // 会員情報設定が必要な場合は、訴求画面に遷移
          encourageRegistrationIfNeeded(customer);
        }
      } catch (e) {
        state.error = e as any;
      }
    });

    return {
      isFooter,
      state,

      showHelpModal,
      toggleHelpModal,
    };
  },
});
